/**
 * `lowest(metric)` — the formula's "least is best" (02/11/2026, the user:
 * "keep the same BUT based on the least instead of highest"). It scores how
 * far an item sits below the HIGHEST value of that metric among the items
 * the ranking ranks (`max − value`): the lowest scores the most, the highest
 * 0, and every score stays ≥ 0 so shares and leans keep working. The ranker
 * hands each group that max (`lowestMax`, read here as `__max_<metric>`);
 * without one (the Flow Optimizer, a lone item) it is `−value` — the same
 * order, because the max is one constant for every item of a range.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { AstNode } from "../ast";

export const LOWEST_FN = "lowest";

const MAX_PREFIX = "__max_";

/** The bag key that carries the ranker's highest value of `metric`. */
export const lowestMaxKey = (metric: string): string => `${MAX_PREFIX}${metric}`;

export const isLowestMaxKey = (key: string): boolean => key.startsWith(MAX_PREFIX);

/** `max − value` with the ranker's max, else `−value` (missing values read 0). */
export function lowestValue(metrics: Record<string, number>, metric: string): number {
  const value = finite(metrics[metric]) ?? 0;
  const max = finite(metrics[lowestMaxKey(metric)]);
  return max === undefined ? -value : max - value;
}

/** The bag with each `lowestMax` entry under its `__max_` key. */
export function withLowestMax(
  metrics: Record<string, number>,
  lowestMax: Record<string, number> | undefined,
): Record<string, number> {
  if (!lowestMax || Object.keys(lowestMax).length === 0) return metrics;
  const out = { ...metrics };
  for (const [metric, max] of Object.entries(lowestMax)) out[lowestMaxKey(metric)] = max;
  return out;
}

/** The parser's rule: `lowest()` takes exactly one metric, nothing else. */
export function assertLowestArgs(args: AstNode[], position: number): void {
  if (args.length === 1 && args[0].kind === "ident") return;
  throw new Error(`Formula parse error at ${position}: lowest() takes one metric`);
}

function finite(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}
